// MIDAS V3 — Streaming state (Phase 6)
//
// Replaces the Phase 2 minimal `StreamingPlaceholder` (in
// ChatMessageListV3) with the full streaming chrome from the handoff:
//   • Header row: animated MidasLogo (streaming + halo) + "Midas" + uppercase
//     `streaming` chip + dotted timestamp.
//   • Step chip: rounded pill, accent-soft bg, accent-line border, accent
//     text. Contains a 6×6 pulsing dot, the current step label (uppercase
//     mono), and a left→right shimmer sweep clipped by overflow:hidden.
//   • Elapsed timer: mono 11.5 muted, ticks every 100ms.
//
// Pre-flight finding (documented in main session report):
//   • `ChatMessage.status` does NOT exist on the chat-bot model. The
//     spec assumed a `message.status === "streaming"` field that the
//     codebase doesn't actually have.
//   • `message.currentStep` does NOT exist either (Q-MIDAS-V3-4: pending
//     bot-side emit).
//   • Streaming is tracked via `state.chatBot.threadBuffers[threadId]`
//     which the buffer-scoped MIDAS-SCOPESTREAM pattern from chatBotSlice
//     populates. The buffer has `isLoading`, `streamingText`, `currentStep`.
//
// So this component reads ONLY from the thread buffer (passed in via
// props), not from a message. The parent (ChatMessageListV3) decides when
// to mount it — same place the v2 plumbing has been firing all along.
//
// Step label fallback (elapsed-time bucket per spec §2):
//   • 0–1s   → ANALYZUJEM VAŠU OTÁZKU…
//   • 1–3s   → HĽADÁM V SCHÉMACH…
//   • 3–7s   → OVERUJEM PLC HODNOTY…
//   • 7s+    → GENERUJEM ODPOVEĎ…
// Localised via chatBot.midas.v3.streaming.steps.{analyzing,searching,
// verifyingPlc,generating}.
//
// Reduced-motion: all 3 motion sources (halo pulse, dot pulse, shimmer) are
// gated either by JS (in MidasLogoV3) or by `@media (prefers-reduced-motion:
// no-preference)` in midas-v3-tokens.scss. The global override in the same
// sheet (animation-duration: 0.001ms) is the belt+braces fallback for any
// inline animation we add here.
//
// Spec: MIDAS_V3_PHASE6_STREAMING_ACTIONS_SPEC.md §2
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useEffect, useRef, useState } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import MidasLogoV3 from "./MidasLogoV3";

export interface StreamingStateV3Props {
  /** Backend-supplied step key (e.g. "analyzing"), if any. When undefined the
   *  component falls back to elapsed-time bucket labels. */
  currentStep?: string | null;
  /** Override the elapsed seconds value (for docs / tests). Bypasses the
   *  internal interval entirely. */
  staticElapsed?: number;
  /** Optional epoch ms of when the stream started. Defaults to the mount
   *  time of this component. Allows callers (e.g. the message list) to
   *  preserve elapsed time across re-renders. */
  startedAt?: number;
}

// ─── Elapsed-time fallback step buckets ────────────────────────────────
const pickFallbackStep = (
  elapsedSec: number,
  steps: { analyzing: string; searching: string; verifyingPlc: string; generating: string }
): string => {
  if (elapsedSec < 1) return steps.analyzing;
  if (elapsedSec < 3) return steps.searching;
  if (elapsedSec < 7) return steps.verifyingPlc;
  return steps.generating;
};

// ─── Map an explicit bot step key to one of the four buckets ───────────
// The bot SSE emits step keys like "analyzing" / "searching" / etc. Today
// we don't see these (Q-MIDAS-V3-4). When the bot starts emitting them we
// translate using the same string set as the fallback. Unknown keys pass
// through verbatim so we don't hide what the bot is saying — uppercased
// to fit the chip style.
const resolveStepLabel = (
  step: string | null | undefined,
  elapsedSec: number,
  steps: { analyzing: string; searching: string; verifyingPlc: string; generating: string }
): string => {
  if (!step) return pickFallbackStep(elapsedSec, steps);
  const key = step.toLowerCase();
  if (key === "analyzing") return steps.analyzing;
  if (key === "searching") return steps.searching;
  if (key === "verifying_plc" || key === "verifyingplc") return steps.verifyingPlc;
  if (key === "generating") return steps.generating;
  // Unknown — uppercase so it matches the chip style.
  return step.toUpperCase();
};

const formatTime = (ts: number): string => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const StreamingStateV3: React.FC<StreamingStateV3Props> = ({
  currentStep,
  staticElapsed,
  startedAt,
}) => {
  const { translate } = useTranslation("chatBot");

  // Anchor elapsed to mount time unless the caller passed an explicit value.
  const startedAtRef = useRef<number>(startedAt ?? Date.now());
  // Keep the anchor in sync if the parent provides a fresher one (e.g. when
  // a new stream begins after this component unmounted + remounted).
  useEffect(() => {
    if (typeof startedAt === "number") {
      startedAtRef.current = startedAt;
    }
  }, [startedAt]);

  // ─── 100ms elapsed timer ─────────────────────────────────────────────
  const [elapsed, setElapsed] = useState<number>(() => {
    if (typeof staticElapsed === "number") return staticElapsed;
    return Math.max(0, (Date.now() - startedAtRef.current) / 1000);
  });

  useEffect(() => {
    // Static mode — no interval needed.
    if (typeof staticElapsed === "number") {
      setElapsed(staticElapsed);
      return undefined;
    }
    const tick = () => {
      setElapsed(Math.max(0, (Date.now() - startedAtRef.current) / 1000));
    };
    tick();
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [staticElapsed]);

  // ─── Step label resolution ───────────────────────────────────────────
  const steps = {
    analyzing: translate("midas.v3.streaming.steps.analyzing", {
      defaultValue: "ANALYZUJEM VAŠU OTÁZKU…",
    }) as string,
    searching: translate("midas.v3.streaming.steps.searching", {
      defaultValue: "HĽADÁM V SCHÉMACH…",
    }) as string,
    verifyingPlc: translate("midas.v3.streaming.steps.verifyingPlc", {
      defaultValue: "OVERUJEM PLC HODNOTY…",
    }) as string,
    generating: translate("midas.v3.streaming.steps.generating", {
      defaultValue: "GENERUJEM ODPOVEĎ…",
    }) as string,
  };
  const stepLabel = resolveStepLabel(currentStep, elapsed, steps);

  const streamingLabel = translate("midas.v3.streaming.label", {
    defaultValue: "streaming",
  }) as string;
  const elapsedSuffix = translate("midas.v3.streaming.elapsedSuffix", {
    defaultValue: "s elapsed",
  }) as string;

  return (
    <div
      style={{
        marginBottom: 22,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {/* Header row — animated logo + Midas + streaming chip + timestamp */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <MidasLogoV3 size={26} streaming halo />
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: "var(--ink)",
            fontFamily: "var(--font-sans)",
          }}
        >
          Midas
        </span>
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--accent)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          {streamingLabel}
        </span>
        <span
          className="mono"
          aria-hidden="true"
          style={{
            fontSize: 11,
            color: "var(--faint)",
            letterSpacing: "0.04em",
          }}
        >
          · {formatTime(startedAtRef.current)}
        </span>
      </div>

      {/* Amber-LED hairline — ported from v2's ThinkingLineV2
          (ChatMessageListV2.tsx:118-128). 1px tall, full-column width,
          rounded; left→right sweep + opacity pulse via the
          `midas-v3-led` keyframe defined in midas-v3-tokens.scss
          (gated by prefers-reduced-motion: no-preference). The
          gradient uses var(--accent) at 0.8 alpha so it picks up the
          active palette (terracotta for p-claude, blue for p-machineid). */}
      <div
        className="midas-v3-streaming-led"
        aria-hidden="true"
        style={{
          height: 1,
          width: "100%",
          borderRadius: 999,
          background:
            "linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--accent) 80%, transparent) 50%, transparent 100%)",
          backgroundSize: "200% 100%",
          animation: "midas-v3-led 1.6s ease-in-out infinite",
        }}
      />

      {/* Step chip + elapsed timer row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {/* Pill — overflow:hidden so the shimmer sweep is clipped to the
            chip silhouette. Inline-flex so the dot + label align baseline. */}
        <span
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "5px 12px 5px 10px",
            background: "var(--accent-soft)",
            border: "1px solid var(--accent-line)",
            borderRadius: 999,
            fontSize: 11,
            color: "var(--accent)",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {/* Pulsing dot — 6×6, accent, midas-pulse 1.2s ease-in-out infinite */}
          <span
            aria-hidden="true"
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: "var(--accent)",
              flexShrink: 0,
              animation: "midas-pulse 1.2s ease-in-out infinite",
            }}
          />
          {/* Step label */}
          <span
            className="mono"
            style={{
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontSize: 10.5,
              position: "relative",
              zIndex: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {stepLabel}
          </span>
          {/* Shimmer sweep — absolute, full-bleed, linear gradient.
              Translates -100% → 200% over 2s linear infinite. Clipped by
              the pill's overflow:hidden. */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
              transform: "translateX(-100%)",
              animation: "midas-shimmer 2s linear infinite",
              pointerEvents: "none",
            }}
          />
        </span>

        {/* Elapsed timer — 1 decimal, mono, faint */}
        <span
          className="mono"
          aria-live="polite"
          style={{ fontSize: 11.5, color: "var(--muted)" }}
        >
          {elapsed.toFixed(1)}
          {elapsedSuffix}
        </span>
      </div>
    </div>
  );
};

export default StreamingStateV3;
